import {
  cilMinus,
  cilPlus,
} from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import React, { useState, useEffect } from 'react';

const WATER_SHRINK_PER_POINT = 0.014;

function toMoney(n, places = 2) {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
}

function calcRow(yieldBu, price, phantomPerPoint, halfPrice, moisture) {
  const points = Math.max(0, moisture - 15);
  let postedRatePerWetBu = 0;
  const p = points;
  const first_tier = Math.min(5, p);
  postedRatePerWetBu += first_tier * 0.055;
  const second_tier = Math.max(0, Math.min(5, p - 5));
  postedRatePerWetBu += second_tier * 0.045;
  const third_tier = Math.max(0, p - 10);
  postedRatePerWetBu += third_tier * 0.04;
  const dryingPerWetBu = halfPrice ? postedRatePerWetBu * 0.5 : postedRatePerWetBu;
  const totalDrying = yieldBu * dryingPerWetBu;
  const shrinkedBuWater = yieldBu - yieldBu * points * WATER_SHRINK_PER_POINT;
  const grossAfterWater = shrinkedBuWater * price;
  const gAfterLessDry = grossAfterWater - totalDrying;
  const waitingBu = yieldBu - yieldBu * (phantomPerPoint + WATER_SHRINK_PER_POINT) * points;
  const waitingGross = waitingBu * price;
  const j = gAfterLessDry - waitingGross;
  const phantomLossBu = yieldBu * points * phantomPerPoint;
  const postedPerBu = dryingPerWetBu;
  const deltaPerBu = -(j / yieldBu);

  return {
    m: moisture.toFixed(1),
    pts: points.toFixed(1),
    C: toMoney(dryingPerWetBu, 4),
    D: toMoney(totalDrying, 2),
    E: Number(shrinkedBuWater.toFixed(2)).toLocaleString(),
    F: toMoney(grossAfterWater, 2),
    G: toMoney(gAfterLessDry, 2),
    H: Number(waitingBu.toFixed(2)).toLocaleString(),
    I: toMoney(waitingGross, 2),
    J: toMoney(j, 2),
    L: Number(phantomLossBu.toFixed(2)).toLocaleString(),
    O: toMoney(postedPerBu, 3),
    P: toMoney(deltaPerBu, 3),
    jSign: j >= 0 ? 'ok' : 'bad',
  };
}

function PhantomYieldLossCalculator() {
  const [yieldBu, setYieldBu] = useState(230);
  const [price, setPrice] = useState(4.0);
  const [phantomPerPoint, setPhantomPerPoint] = useState(0.0075); // 0.75%
  const [half, setHalf] = useState(false);
  const [startM, setStartM] = useState(15.0);
  const [endM, setEndM] = useState(30);
  const [rows, setRows] = useState([]);
  const [assumptionsCollapsed, setAssumptionsCollapsed] = useState(false);
  const [bushelLoss, setBushelLoss] = useState(0.0);

  useEffect(() => {
    // Only run once on mount to set initial bushelLoss from default phantomPerPoint
    if (yieldBu > 0 && endM > 15) {
      const points = endM - 15;
      const initialLoss = phantomPerPoint * points * yieldBu;
      setBushelLoss(Number(initialLoss.toFixed(1))); // or .toFixed(2) if you prefer
    }
  }, []);

  useEffect(() => {
    // Only recalculate percentage if user has entered a non-zero bushel loss
    if (bushelLoss > 0 && yieldBu > 0 && endM > 15) {
      const points = endM - 15;
      const newPerPoint = bushelLoss / (points * yieldBu);
      setPhantomPerPoint(Number(newPerPoint.toFixed(6)));
    }
  }, [bushelLoss, yieldBu, endM]);

  useEffect(() => {
    const recalc = () => {
      const newRows = [];
      for (let m = startM; m <= endM + 1e-9; m += 0.5) {
        newRows.push(
          calcRow(yieldBu, price, phantomPerPoint, half, +m.toFixed(1))
        );
      }
      setRows(newRows);
    };
    recalc();
  }, [yieldBu, price, phantomPerPoint, half, startM, endM]);

  return (
    <div>
      <style>{`
        .phantom-yield-container * {
          box-sizing: border-box;
        }

        .phantom-yield-container {
          background: #ffffff;
          color: #111827; /* off-black text */
          font: 15px/1.45 ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial;
        }

        .phantom-yield-container.container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: 100vw;
          padding: 18px;
        }

        .phantom-yield-container .layout {
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-width: 0;
        }

        /* desktop layout: side-by-side from 900px and up */
        @media (min-width: 900px) {
          .phantom-yield-container .layout {
            flex-direction: row;
          }
        }

        .phantom-yield-container .card {
          background: #ffffff;
          border: 1px solid #c5cedd; /* ~10% darker edge */
          border-radius: 16px;
          box-shadow: 0 10px 28px rgba(15,23,42,.06);
        }

        .phantom-yield-container .inputs-card {
          flex: 0 0 auto;
          width: 100%;
        }

        @media (min-width: 900px) {
          .phantom-yield-container .inputs-card { width: 360px; }
        }

        .phantom-yield-container .results-card {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .phantom-yield-container .card h2 {
          margin: 0;
          padding: 14px 16px;
          border-bottom: 1px solid #d6deea;
          font-size: 12px;
          color: #4b5563;
          text-transform: uppercase;
          letter-spacing: .16em;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .phantom-yield-container .card .body {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .phantom-yield-container .table-wrap {
          flex: 1;
          overflow-x: auto;
          overflow-y: auto;
          border: 1px solid #c5cedd;
          border-radius: 14px;
          min-width: 0;
          background: #ffffff;
        }

        .phantom-yield-container label {
          font-size: 12px;
          color: #4b5563;
        }

        .phantom-yield-container input[type='number'] {
          width: 100%;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #d6deea;
          background: #f9fafb;
          color: #111827;
          outline: none;
        }
        .phantom-yield-container input[type='number']:focus {
          border-color: #94a3b8;
          box-shadow: 0 0 0 1px rgba(148,163,184,0.5);
        }

        .phantom-yield-container input[type='range'] {
          width: 100%;
          accent-color: #ff5f00; /* orange slider */
        }

        .phantom-yield-container .grid { display: grid; gap: 10px; }
        .phantom-yield-container .row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

        .phantom-yield-container .pill {
          display: inline-block;
          padding: .2rem .6rem;
          border: 1px solid #d6deea;
          border-radius: 999px;
          color: #374151;
          font-size: 12px;
          background: #f9fafb;
        }

        .phantom-yield-container .kpis { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-top: 8px; }
        .phantom-yield-container .k {
          border: 1px solid #d6deea;
          background: #f9fafb;
          border-radius: 12px;
          padding: 8px 10px;
          color: #111827;
        }
        .phantom-yield-container .k b { display: block; font-size: 16px; color: #111827; }

        /* Don't fight Bootstrap .btn; only style non-Bootstrap buttons */
        .phantom-yield-container button:not(.btn) {
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #d6deea;
          background: #e5e7eb;
          color: #111827;
          cursor: pointer;
        }
        .phantom-yield-container button:not(.btn):hover { filter: brightness(0.98); }

        .phantom-yield-container .assume-toggle {
          margin-top: 12px;
          width: 100%;
          padding: 12px 14px;
          font-weight: 600;
        }

        .phantom-yield-container .assumption-note {
          margin-top: 12px;
          border-radius: 12px;
          border: 1px solid #d6deea;
          background: #f9fafb;
          padding: 10px 12px;
          font-size: 12px;
          color: #374151;
        }

        .phantom-yield-container .phantom-slider-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 8px;
        }
        .phantom-yield-container .phantom-slider-value {
          font-size: 12px;
          color: #111827;
        }

        .phantom-yield-container table {
          width: 100%;
          min-width: 950px; /* ensures horizontal scroll on small viewports */
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed; /* keep grid fitting screen */
        }

        .phantom-yield-container thead th {
          position: sticky;
          top: 0;
          background: #f3f4f6;
          border-bottom: 1px solid #d6deea;
          padding: 6px 6px;
          font-size: 11px;
          color: #4b5563;
          text-align: center;
          white-space: normal;
          line-height: 1.1;
          z-index: 2;
        }

        .phantom-yield-container .band {
          background: #e5e7eb !important;
          color: #111827 !important;
          text-align: center;
          border-bottom: 1px solid #c5cedd;
        }
        .phantom-yield-container .band-a { background: #f7f8fb !important; }
        .phantom-yield-container .band-b { background: #f3f5f9 !important; }
        .phantom-yield-container .band-c { background: #eceff5 !important; }

        .phantom-yield-container tbody td {
          padding: 6px 6px;
          border-bottom: 1px dashed rgba(148,163,184,.6);
          text-align: center;
          white-space: nowrap;
          font-size: 12px;
          line-height: 1.2;
          color: #111827;
        }
        .phantom-yield-container tbody tr:nth-child(odd) {
          background: rgba(15,23,42,0.04);
        }

        /* Group shading: 5 shades for the 5 column groups */
        .phantom-yield-container td.g1,
        .phantom-yield-container th.g1 { background: #f7f8fb; }
        .phantom-yield-container td.g2,
        .phantom-yield-container th.g2 { background: #f3f5f9; }
        .phantom-yield-container td.g3,
        .phantom-yield-container th.g3 { background: #eceff5; }
        .phantom-yield-container td.g4,
        .phantom-yield-container th.g4 { background: #e6e9f2; }
        .phantom-yield-container td.g5,
        .phantom-yield-container th.g5 { background: #e1e5f0; }

        .phantom-yield-container .ok { color: #079e3eff; }
        .phantom-yield-container .bad { color: #c50a0aff; }

        /* Spacer columns as shaded vertical dividers */
        .phantom-yield-container th.spacer-col,
        .phantom-yield-container td.spacer-col {
          width: 5px;
          min-width: 5px;
          padding: 0;
          background: #d6deea;
          border-left: 1px solid #c5cedd;
          border-right: 1px solid #c5cedd;
          box-shadow: inset 0 0 0 1px rgba(148,163,184,0.3);
        }

        /* Sticky leading columns (1–3), width slightly reduced (~5%) */
        .phantom-yield-container .sticky-col-1,
        .phantom-yield-container .sticky-col-2,
        .phantom-yield-container .sticky-col-3 {
          position: sticky;
          z-index: 3;
        }

        /* base width ~62px each */
        .phantom-yield-container .sticky-col-1 {
          left: 0;
        }

        .phantom-yield-container .sticky-col-2 {
          left: 62px;   /* width of col 1 */
        }

        .phantom-yield-container .sticky-col-3 {
          left: 124px;  /* width of col 1 + col 2 */
        }

        .phantom-yield-container th.sticky-col-1,
        .phantom-yield-container td.sticky-col-1,
        .phantom-yield-container th.sticky-col-2,
        .phantom-yield-container td.sticky-col-2,
        .phantom-yield-container th.sticky-col-3,
        .phantom-yield-container td.sticky-col-3 {
          min-width: 62px;
          background: #f7f8fb;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        /* Mobile-only caret toggle button */
        .phantom-yield-container .inputs-toggle {
          display: none;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          width: 24px;
          height: 24px;
          border-radius: 999px;
          padding: 0;
          border: 1px solid #d6deea;
          background: #f9fafb;
          color: #4b5563;
        }

        .phantom-yield-container .inputs-toggle:focus {
          outline: none;
          box-shadow: 0 0 0 1px rgba(148,163,184,0.6);
        }

        /* Slider base track */
        .phantom-yield-container input[type='range'] {
          width: 100%;
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          border-radius: 999px;
          border: none;
          background: transparent; /* gradient is inline */
        }

        /* Chrome / Edge / Safari */
        .phantom-yield-container input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #ff5f00;
          border: none;
          cursor: pointer;
          margin-top: calc((6px - 8px) / 2);
        }

        /* Firefox track */
        .phantom-yield-container input[type='range']::-moz-range-track {
          height: 6px;
          border-radius: 999px;
          background: transparent;
        }

        /* Firefox thumb */
        .phantom-yield-container input[type='range']::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #ff5f00;
          border: none;
          cursor: pointer;
          margin-top: 0; /* Firefox centers by default */
        }

        /* --- RESPONSIVE / MOBILE BEHAVIOR --- */

        @media (max-width: 899px) {
          /* Remove outer padding on small screens */
          .phantom-yield-container.container {
            padding: 0;
          }

          /* Inputs card full-width, stacked content, tighter padding/margins */
          .phantom-yield-container .inputs-card {
            width: 100%;
            max-width: none !important;
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .phantom-yield-container .card .body {
            padding: 8px; /* ~50% of 16px */
          }

          .phantom-yield-container .grid {
            gap: 5px; /* ~50% of 10px */
          }

          .phantom-yield-container .row {
            gap: 5px;
          }

          /* Assumptions panel: vertical stack, no horizontal scroll */
          .phantom-yield-container .inputs-card .body > .grid {
            display: grid;
          }

          .phantom-yield-container .assume-toggle {
            margin-top: 6px; /* reduce margin */
            padding: 8px 10px; /* shorter button */
          }

          .phantom-yield-container .assumption-note {
            margin-top: 6px;
            padding: 6px 8px;
          }

          .phantom-yield-container .results-card {
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          /* Show caret only on mobile/tablet */
          .phantom-yield-container .inputs-toggle {
            display: inline-flex;
          }

          /* Collapse behavior for assumptions panel */
          .phantom-yield-container .inputs-card.collapsed .body {
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            overflow: hidden;
          }
        }

        /* Disable sticky at very small widths (<= 390px) */
        @media (max-width: 390px) {
          .phantom-yield-container .sticky-col-1,
          .phantom-yield-container .sticky-col-2,
          .phantom-yield-container .sticky-col-3,
          .phantom-yield-container th.sticky-col-1,
          .phantom-yield-container th.sticky-col-2,
          .phantom-yield-container th.sticky-col-3,
          .phantom-yield-container td.sticky-col-1,
          .phantom-yield-container td.sticky-col-2,
          .phantom-yield-container td.sticky-col-3 {
            position: static;
            left: auto;
            min-width: auto;
          }
        }
      `}</style>

      <div className="container phantom-yield-container">
        <div className="layout">
          {/* Inputs & Assumptions – fixed size on desktop, full-width on mobile */}
          <div
            className={
              'card inputs-card' + (assumptionsCollapsed ? ' collapsed' : '')
            }
            style={{ maxWidth: '17.5%' }}
          >
            <h2>
              <span>Inputs & Assumptions</span>
              <CIcon
                icon={assumptionsCollapsed ? cilPlus : cilMinus}
                style={{ color: 'black', height: '22px', cursor: 'pointer' }}
                onClick={() => setAssumptionsCollapsed(v => !v)}
                aria-label={assumptionsCollapsed ? 'Expand assumptions' : 'Collapse assumptions'}
              />
            </h2>
            <div className="body">
              <div className="grid">
                {/* 1: Yield */}
                <div className="grid">
                  <label>Yield (bu/acre)</label>
                  <input
                    type="number"
                    step="1"
                    value={yieldBu}
                    onChange={(e) => setYieldBu(parseFloat(e.target.value))}
                  />
                </div>

                {/* 2: Price */}
                <div className="grid">
                  <label>Price of Corn ($/bu)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(parseFloat(e.target.value))}
                  />
                </div>

                {/* Bushel Loss (total extra bu/acre) – user can type here */}
                <div className="grid">
                  <label>Bushel Loss Between Lower & Upper Moisture</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={bushelLoss}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setBushelLoss(val);

                      // When user types in bushel loss → auto-update phantom %
                      if (val > 0 && yieldBu > 0 && endM > 15) {
                        const points = endM - 15;
                        const perPoint = val / (points * yieldBu);
                        setPhantomPerPoint(Number(perPoint.toFixed(6))); // higher precision to avoid rounding issues
                      } else if (val === 0) {
                        // Optional: reset to a default or do nothing
                        // setPhantomPerPoint(0.0075); // or keep current
                      }
                    }}
                  />
                </div>

                {/* Phantom Loss Per Point slider – user can drag here */}
                <div className="grid">
                  <div className="phantom-slider-header">
                    <label>Phantom Loss Per Point of Moisture</label>
                    <span className="phantom-slider-value">
                      {(phantomPerPoint * 100).toFixed(2)}%
                    </span>
                  </div>

                  {(() => {
                    const min = 0.002;   // 0.2%
                    const max = 0.015;   // 1.5%
                    const clampedValue = Math.max(min, Math.min(max, phantomPerPoint));
                    const percent = ((clampedValue - min) / (max - min)) * 100;

                    return (
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={0.0001}
                        value={clampedValue}
                        onChange={(e) => {
                          const newPerPoint = parseFloat(e.target.value) || 0;
                          setPhantomPerPoint(newPerPoint);

                          // When slider moves → auto-update total bushel loss
                          if (yieldBu > 0 && endM > 15) {
                            const points = endM - 15;
                            const calculatedLoss = newPerPoint * points * yieldBu;
                            setBushelLoss(Number(calculatedLoss.toFixed(2))); // round to sensible precision
                          }
                        }}
                        style={{
                          background: `linear-gradient(to right,
                              #ff5f00 0%,
                              #ff5f00 ${percent}%,
                              #d4d4d8 ${percent}%,
                              #d4d4d8 100%
                            )`,
                        }}
                      />
                    );
                  })()}

                  {/* Small hint text */}
                  <div style={{ fontSize: '11px', color: '#4b5563', marginTop: 4, lineHeight: 1.3 }}>
                    Type bushels above to auto-set % • or drag slider to update bushels
                  </div>
                </div>

                {/* 4: Start/End moisture row below slider */}
                <div className="row">
                  <div className="grid">
                    <label>Lower Moisture (%)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={startM}
                      onChange={(e) => setStartM(parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="grid">
                    <label>Upper Moisture (%)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={endM}
                      onChange={(e) => setEndM(parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                {/* 5: Button below, height reduced in mobile */}
                <button
                  type="button"
                  className="assume-toggle btn btn-outline-primary"
                  onClick={() => setHalf((h) => !h)}
                >
                  {half ? 'Assume ½ Price Drying (On)' : 'Assume ½ Price Drying'}
                </button>

                {/* 6: Blurb below */}
                <div className="assumption-note">
                  Water shrink per point of moisture is assumed to be 1.4% and Phantom Loss is
                  additional forms of loss that is hybrids &amp; environment specific.
                </div>
              </div>
            </div>
          </div>

          {/* Results – takes all remaining width */}
          <div className="card results-card">
            <h2>Results</h2>
            <div className="body">
              <div className="table-wrap">
                <table>
                  <thead>
                    {/* Macro headers with 5 groups + spacers */}
                    <tr>
                      {/* Group 1 (cols 1–4): 4 individual cells so widths align with sticky cols */}
                      <th className="band band-a g1 sticky-col-1"></th>
                      <th className="band band-a g1 sticky-col-2"></th>
                      <th className="band band-a g1 sticky-col-3"></th>
                      <th className="band band-a g1"></th>

                      {/* Spacer between group 1 and 2 */}
                      <th className="spacer-col"></th>

                      {/* Group 2 (cols 5–7) */}
                      <th className="band band-b g2" colSpan={3}>
                        If Paying Commercial Drying
                      </th>

                      {/* Spacer between group 2 and 3 */}
                      <th className="spacer-col"></th>

                      {/* Group 3 (cols 8–10) */}
                      <th className="band band-b g3" colSpan={3}>
                        If Waiting for Natural Dry w/ Phantom
                      </th>

                      {/* Spacer between group 3 and 4 */}
                      <th className="spacer-col"></th>

                      {/* Group 4 (col 11) – blank macro (gap over Phantom Bu Lost) */}
                      <th className="band band-a g4"></th>

                      {/* Spacer between group 4 and 5 */}
                      <th className="spacer-col"></th>

                      {/* Group 5 (cols 12–13) */}
                      <th className="band band-c g5" colSpan={2}>
                        Per Bushel
                      </th>
                    </tr>

                    {/* Micro headers: 13 data columns + 4 spacers = 17 cells */}
                    <tr>
                      {/* Group 1 (1–4) */}
                      <th className="g1 sticky-col-1">Moisture %.</th>
                      <th className="g1 sticky-col-2">{'Points >15%.'}</th>
                      <th className="g1 sticky-col-3">Drying $/wet bu.</th>
                      <th className="g1">Total Drying $ (per acre).</th>

                      {/* Spacer 1 */}
                      <th className="spacer-col"></th>

                      {/* Group 2 (5–7) */}
                      <th className="g2">Shrinked Bu.– 1.4% Shrink Per Point.</th>
                      <th className="g2">Revenue if Deliver Now ($).</th>
                      <th className="g2">Net if Deliver Now ($).</th>

                      {/* Spacer 2 */}
                      <th className="spacer-col"></th>

                      {/* Group 3 (8–10) */}
                      <th className="g3">Bu. With Phantom Loss.</th>
                      <th className="g3">Revenue Phantom Loss.</th>
                      <th className="g3">Advantage to Commercial Drying/Acre.</th>

                      {/* Spacer 3 */}
                      <th className="spacer-col"></th>

                      {/* Group 4 (11) */}
                      <th className="g4">Phantom Bushels Lost Above Moisture Loss.</th>

                      {/* Spacer 4 */}
                      <th className="spacer-col"></th>

                      {/* Group 5 (12–13) */}
                      <th className="g5">Posted Cost.</th>
                      <th className="g5">Actual Cost to Commercial Drying.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        {/* Group 1 (1–4) */}
                        <td className="g1 sticky-col-1">{r.m}</td>
                        <td className="g1 sticky-col-2">{r.pts}</td>
                        <td className="g1 sticky-col-3">{r.C}</td>
                        <td className="g1">{r.D}</td>

                        {/* Spacer 1 */}
                        <td className="spacer-col"></td>

                        {/* Group 2 (5–7) */}
                        <td className="g2">{r.E}</td>
                        <td className="g2">{r.F}</td>
                        <td className="g2">{r.G}</td>

                        {/* Spacer 2 */}
                        <td className="spacer-col"></td>

                        {/* Group 3 (8–10) */}
                        <td className="g3">{r.H}</td>
                        <td className="g3">{r.I}</td>
                        <td className={`g3 ${r.jSign}`}>{r.J}</td>

                        {/* Spacer 3 */}
                        <td className="spacer-col"></td>

                        {/* Group 4 (11) */}
                        <td className="g4">{r.L}</td>

                        {/* Spacer 4 */}
                        <td className="spacer-col"></td>

                        {/* Group 5 (12–13) */}
                        <td className="g5">{r.O}</td>
                        <td className="g5">{r.P}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhantomYieldLossCalculator;
